/**
 * One build, many subdomains.
 *
 * Every module shares the approval engine, the shell, the auth flow
 * and every component. Shipping a bundle per module would mean fixing
 * each bug once per copy, so the module is decided at runtime from the
 * hostname and the same dist is deployed everywhere.
 *
 * In development there is no subdomain, so ?module=eng overrides.
 */
export type ModuleKey =
  | 'PM'
  | 'ENG'
  | 'PROC'
  | 'PROG'
  | 'CX'
  | 'COSTM'
  | 'QM'
  | 'DELAY'
  | 'TEAM'
  | 'HSE';

const BY_HOST: Record<string, ModuleKey> = {
  pm: 'PM',
  eng: 'ENG',
  proc: 'PROC',
  prog: 'PROG',
  cx: 'CX',
  cost: 'COSTM',
  qa: 'QM',
  delay: 'DELAY',
  team: 'TEAM',
  hse: 'HSE',
};

function detect(): ModuleKey {
  const override = new URLSearchParams(location.search).get('module');
  if (override) return BY_HOST[override.toLowerCase()] ?? (override.toUpperCase() as ModuleKey) ?? 'PM';
  return BY_HOST[location.hostname.split('.')[0] ?? ''] ?? 'PM';
}

export const MODULE: ModuleKey = detect();

type Meta = {
  key: ModuleKey;
  /** Browser title and page heading. */
  title: string;
  /** What one row is called, in this module's language. */
  noun: string;
  nounPlural: string;
  /** Document.kind this module owns, when it owns one. */
  kind: 'DOCUMENT' | 'DELIVERABLE';
  /** Deliverables climb IFR -> IFC; plain documents have no stage. */
  usesStages: boolean;
  /** Groups the register by discipline instead of one flat list. */
  groupByDiscipline: boolean;
  /** Built yet? A module that is not gets an honest sign-in page. */
  live: boolean;
  /**
   * Views in the second nav group.
   *
   * Held here rather than branched inside Shell: a module that adds
   * a screen should not require editing the shell, and the shell
   * should not carry a growing list of module names.
   */
  views: { to: string; label: string }[];
  emptyBody: string;
  /**
   * Sign-in copy. Odin kept two ravens: Hugin, thought, and Munin,
   * memory. Each module is tied to the piece of that story its work
   * actually resembles — not decoration, and deliberately restrained.
   */
  welcome: { eyebrow: string; heading: string; body: string; note: string };
};

const M = (m: Meta) => m;

export const META: Record<ModuleKey, Meta> = {
  PM: M({
    key: 'PM',
    title: 'Document Control',
    noun: 'document',
    nounPlural: 'Documents',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Register' },
    ],
    emptyBody: 'No documents have been registered on this project yet.',
    welcome: {
      eyebrow: 'Munin — memory',
      heading: 'What was issued, who reviewed it, and what they said.',
      body:
        'Odin kept a raven whose only task was to carry memory back to him. This module has the same job. Every revision leaves a name, a date and a review code, and none of it is ever overwritten.',
      note: 'Superseded revisions keep their comments. That is the point of them.',
    },
  }),

  ENG: M({
    key: 'ENG',
    title: 'Engineering Tracking',
    noun: 'deliverable',
    nounPlural: 'Deliverables',
    kind: 'DELIVERABLE',
    usesStages: true,
    groupByDiscipline: true,
    live: true,
    views: [
      { to: '/', label: 'Register' },
      { to: '/overdue', label: 'Blocking' },
    ],
    emptyBody: 'No deliverables have been registered on this project yet.',
    welcome: {
      eyebrow: 'The daily flight',
      heading: 'Every discipline, every morning, nothing unaccounted for.',
      body:
        'The ravens went out over all the worlds at dawn and were back before dusk. A deliverable register does the same across civil, mechanical, electrical and the rest — IFR through IFC, with structural calculation carried as a deliverable like any other.',
      note: 'The useful question is never how many drawings exist. It is which one is holding up the field.',
    },
  }),

  PROG: M({
    key: 'PROG',
    title: 'Progress Assurance',
    noun: 'activity',
    nounPlural: 'Progress',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/programme', label: 'Programme' },
      { to: '/claims', label: 'Claims' },
    ],
    emptyBody: 'No progress claim has been opened for this project yet.',
    welcome: {
      eyebrow: 'Hlidskjalf — the high seat',
      heading: 'Look for yourself, rather than being told.',
      body:
        'Odin kept a seat from which every realm was visible at once. Verified progress is that seat, made ordinary: weighted quantities confirmed in the field, not a percentage forwarded from the site office.',
      note: 'Planned and actual are kept apart. The distance between them is the finding.',
    },
  }),

  COSTM: M({
    key: 'COSTM',
    title: 'Cost Assurance',
    noun: 'cost item',
    nounPlural: 'Cost',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/contracts', label: 'Contracts' },
    ],
    emptyBody: 'No contract has been set up on this project yet.',
    welcome: {
      eyebrow: "Mimir's well",
      heading: 'Knowing what it truly costs is never free.',
      body:
        'Odin traded an eye for a single drink from the well of wisdom. The price here is smaller and duller: recording value against work that was verified, rather than against the invoice that happened to arrive.',
      note: 'A forecast built on what exists is one you can defend line by line.',
    },
  }),

  QM: M({
    key: 'QM',
    title: 'Quality Management',
    noun: 'inspection',
    nounPlural: 'Quality',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: true,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/itps', label: 'ITP' },
      { to: '/ncrs', label: 'NCR' },
    ],
    emptyBody: 'No ITP or NCR has been raised on this project yet.',
    welcome: {
      eyebrow: 'Heimdall — the watchman',
      heading: 'Nothing passes for finished that nobody looked at.',
      body:
        'Heimdall was said to hear the grass growing and see a hundred leagues out. A quality gate is far less dramatic and considerably more useful: inspection and test plans sitting on the same activities that carry progress and cost.',
      note: 'A failed ITP blocks certification here, instead of surfacing at handover.',
    },
  }),

  DELAY: M({
    key: 'DELAY',
    title: 'Forensic Delay Analysis',
    noun: 'window',
    nounPlural: 'Delay analysis',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/events', label: 'Peristiwa' },
      { to: '/new', label: 'Analisis baru' },
    ],
    emptyBody: 'No delay analysis has been prepared on this project yet.',
    welcome: {
      eyebrow: 'The Norns — what became',
      heading: 'Where the time went, and who carried the risk.',
      body:
        'Three sisters worked at the roots of the tree: what became, what is becoming, what shall be. Forensic analysis claims only the first. It reads the as-built record window by window and says where the programme actually slipped.',
      note: 'It ends in a mitigation plan built on the recovery available, not the convenient one.',
    },
  }),

  PROC: M({
    key: 'PROC',
    title: 'Procurement',
    noun: 'package',
    nounPlural: 'Packages',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/packages', label: 'Paket' },
      { to: '/orders', label: 'Purchase order' },
      { to: '/deliveries', label: 'Pengiriman' },
    ],
    emptyBody: 'No procurement package has been set up on this project yet.',
    welcome: {
      eyebrow: 'Draupnir — the ring that made rings',
      heading: 'A late approval is a late delivery, and the arithmetic should say so.',
      body:
        "Odin's ring dropped eight more of its own kind every ninth night, and the wealth was in the sequence rather than the gold. Procurement runs the same way: approval releases the order, the order releases the factory, the factory releases the site.",
      note: 'Every delivery note counts against the ordered quantity. What is short stays short until it arrives.',
    },
  }),

  CX: M({
    key: 'CX',
    title: 'Commissioning',
    noun: 'test',
    nounPlural: 'Commissioning',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/register', label: 'Register peralatan' },
      { to: '/levels', label: 'CxL1 - L5' },
    ],
    emptyBody: 'No equipment has been registered for commissioning yet.',
    welcome: {
      eyebrow: 'Gjallarhorn — the horn that must sound',
      heading: 'Installed is not the same as working.',
      body:
        'Heimdall could hear grass grow and kept a horn that had to sound when it mattered. Commissioning is the same discipline applied to plant: every device proven at every level, in order, and never on the word of the person who installed it.',
      note: 'A result without a test report does not move the status. That is the point of it.',
    },
  }),

  TEAM: M({
    key: 'TEAM',
    title: 'Resource Control',
    noun: 'person',
    nounPlural: 'Team',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: false,
    live: true,
    views: [
      { to: '/', label: 'Dashboard' },
      { to: '/week', label: 'Papan mingguan' },
      { to: '/people', label: 'Orang' },
    ],
    emptyBody: 'No resources have been mapped to people on this project yet.',
    welcome: {
      eyebrow: 'The Einherjar — named, not counted',
      heading: 'Who is doing what, this week.',
      body:
        'The hall took warriors one at a time and knew each by name; a crowd would have been useless to it. A programme that says "cabling crew" cannot be asked a question. A programme that says who can.',
      note: 'Activities with nobody named against them are the finding, not the omission.',
    },
  }),

  HSE: M({
    key: 'HSE',
    title: 'HSE Manpower Tracking',
    noun: 'worker',
    nounPlural: 'Manpower',
    kind: 'DOCUMENT',
    usesStages: false,
    groupByDiscipline: true,
    live: false,
    views: [],
    emptyBody: 'HSE manpower tracking is not built yet.',
    welcome: {
      eyebrow: 'Munin, and the fear of not returning',
      heading: 'Counting people onto a site is the easy half.',
      body:
        'Odin sent both ravens out each day and admitted he feared most that Munin would not come back. Headcount in is arithmetic. Headcount out is the reason this module exists — with certificates checked at the gate rather than in a folder.',
      note: 'Man-hours here are the denominator of every safety statistic. They should not be typed in by hand.',
    },
  }),
};

/**
 * Urutan tampil menu: mengikuti ALUR KERJA, bukan urutan pembangunan.
 *
 * Dokumen -> engineering -> pengadaan -> progres -> commissioning ->
 * biaya -> mutu -> keterlambatan -> orang -> HSE. Urutan itu juga yang
 * dipakai landing page, dan menu yang urutannya berbeda dari halaman
 * penjualannya memaksa orang belajar dua peta untuk satu produk.
 *
 * Object.keys(META) memberi urutan penulisan objeknya, yang kebetulan
 * urutan modulnya dibangun — PROC, CX dan TEAM menempel di ekor karena
 * ditambahkan belakangan. Jadi urutannya dinyatakan di sini.
 *
 * Penjaganya ada di baris berikutnya: menambah modul ke ModuleKey
 * tanpa menambahkannya ke sini adalah galat kompilasi, bukan modul
 * yang diam-diam hilang dari menu.
 */
export const MODULE_ORDER = [
  'PM',
  'ENG',
  'PROC',
  'PROG',
  'CX',
  'COSTM',
  'QM',
  'DELAY',
  'TEAM',
  'HSE',
] as const;

type _OrderIsComplete = Exclude<ModuleKey, (typeof MODULE_ORDER)[number]> extends never
  ? true
  : ['modul ini belum ada di MODULE_ORDER:', Exclude<ModuleKey, (typeof MODULE_ORDER)[number]>];
const _orderCheck: _OrderIsComplete = true;
void _orderCheck;

export const meta = META[MODULE];

/**
 * Sibling module URL, so the nav can actually move between them.
 *
 * `path` carries the current view across the hop — preview stays in
 * preview when you switch modules, rather than dropping you at a
 * login form halfway through reviewing the design.
 */
export function moduleHref(target: ModuleKey, path = '/'): string {
  const sub = Object.entries(BY_HOST).find(([, k]) => k === target)?.[0] ?? 'pm';
  const host = location.hostname;
  const first = host.split('.')[0] ?? '';
  if (first in BY_HOST) {
    const rest = host.split('.').slice(1).join('.');
    return `${location.protocol}//${sub}.${rest}${path}`;
  }
  // Development: stay on this origin and switch by query string.
  return `${path}?module=${sub}`;
}
